import React, { useContext, useEffect } from "react";
import { useObserver } from "mobx-react";
import {
  Grid,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Typography,
  Divider
} from "@material-ui/core";
import Select from "@common/Select";
import { FormField } from "@modules/formx-react";
import locale from "@bonliva-crm/locale";
import { NestedExpertiseSelect } from "@containers/NestedExpertiseSelect/NestedExpertiseSelect";
import { PrimaryButton, Topbar } from "@components";
import { CurrencyText, FormxDebugger } from "@components/formx";
import { FormControls } from "@components/forms";
import { CustomerContractPriceListFormContext } from "../CustomerContractPriceListFormContext";
import { useNavigation } from "@navigation";
import { ContractsPriceTemplateType } from "@graphql";
import { decodeProfAoeSpec } from "@bonliva-crm/common/ConsultantSearch";
import { cloneDeep, compact } from "lodash";
import { CustomerContractPriceListFormData } from "@contracts/CustomerContractPriceListEditor/CustomerContractPriceListFormData";
import { ContractsPriceTemplateEditorForm } from "../../../config_editor/ContractsPriceTemplates/ContractsPriceEditorForm";
import { getLastLvlOfAOE } from "../../../config_editor/ContractsPriceTemplates/Search/components/CustomerContractPriceRow";
import { mergeHighHolidays } from "../../../config_editor/ContractsPriceTemplates/utils";
import { ContractPriceTemplateRecordInput } from "../../../config_editor/ContractsPriceTemplates/ContractsPriceTemplateFormInput";

type Props = {
  priceGroupId: string;
};

export function PriceGroupForm(props: Props) {
  const editorStore = useContext(CustomerContractPriceListFormContext);
  const priceGroupIndex = editorStore.getPriceGroupIndexById(props.priceGroupId);
  const nav = useNavigation();

  if (priceGroupIndex < 0) {
    throw new Error("invalid price group id");
  }

  const name = `priceGroups.${priceGroupIndex}`;

  useEffect(() => {
    editorStore.validateContractOpt(editorStore.form.getValue(`${name}.records`));
    editorStore.form.validate();
  }, []);

  const handleChange = (v) => {
    const prevVal = editorStore.form.getValue(`${name}.professionsPrice`);
    const newVal = v.map((x, i) => {
      const temp = prevVal?.find((p) => p.profession === x);
      return { profession: x, price: temp?.price || 0 };
    });
    editorStore.form.setValue(`${name}.professions`, v);
    editorStore.form.setValue(`${name}.professionsPrice`, newVal);
  };

  const setDefaultHolidaysAndSalaryRecords = () => {
    const mergedHolidays = mergeHighHolidays(editorStore.highHolidaysOptions, []);
    editorStore.form.setValue(`${name}.records`, [
      ContractPriceTemplateRecordInput.CreateBlank()
    ]);
    editorStore.form.setValue(`${name}.highHolidays`, mergedHolidays);
  };

  const handleChangeTemplate = (v) => {
    if (!v) {
      editorStore.form.setValue(`${name}.template`, v);
      setDefaultHolidaysAndSalaryRecords();
      return;
    }
    const template = editorStore.templates.find((x) => x._id === v);
    const t = cloneDeep(template);
    const mergedHolidays = mergeHighHolidays(
      editorStore.highHolidaysOptions,
      template.highHolidays
    );
    editorStore.form.setValue(`${name}.template`, v);
    editorStore.form.setValue(`${name}.favTemplate`, null);
    editorStore.form.setValue(`${name}.records`, t.records);
    editorStore.form.setValue(`${name}.highHolidays`, mergedHolidays);
  };

  const handleChangeFavTemplate = (v) => {
    if (!v) {
      editorStore.form.setValue(`${name}.favTemplate`, v);
      setDefaultHolidaysAndSalaryRecords();
      return;
    }
    const template = editorStore.fav.find((x) => x._id === v);
    const t = cloneDeep(template);
    const mergedHolidays = mergeHighHolidays(
      editorStore.highHolidaysOptions,
      template.highHolidays
    );
    editorStore.form.setValue(`${name}.favTemplate`, v);
    editorStore.form.setValue(`${name}.template`, null);
    editorStore.form.setValue(`${name}.records`, t.records);
    editorStore.form.setValue(`${name}.highHolidays`, mergedHolidays);
  };

  const priceGroupsAoes = compact(
    editorStore.form
      .getValue(`priceGroups`)
      .filter((x, i) => i !== priceGroupIndex) // get all blocks except current
      ?.flatMap((x) => x.professions) // flat groups professions
      ?.map((pas) => {
        if (pas) {
          return !decodeProfAoeSpec(pas)[2] && decodeProfAoeSpec(pas)[1];
        }
      })
  ); // get aoe that don't have any spec

  return useObserver(() => (
    <Grid>
      <div style={{ padding: "8px 16px" }}>
        <Topbar>
          <Typography variant="h6">{locale.PriceBlock}</Typography>
        </Topbar>
      </div>
      <Divider />

      <Grid item xs={12} style={{ padding: 16 }}>
        <FormField name={`${name}.professions`}>
          {(field) => (
            <NestedExpertiseSelect
              error={field.error}
              fullWidth
              onChange={handleChange}
              value={field.value}
              filterProfessions={(x) => {
                return editorStore.professions.includes(x);
              }}
              filterSpecializations={(x) => {
                return false;
              }}
              filterAoe={(x) => {
                return !priceGroupsAoes.includes(x.Name);
              }}
            />
          )}
        </FormField>
      </Grid>

      <Grid item xs={5} style={{ padding: 16 }}>
        <Select
          // clearable={false}
          name={`${name}.template`}
          key={`${name}.template`}
          label={locale.Template}
          fullWidth
          value={editorStore.form.getValue(`${name}.template`)}
          onChange={handleChangeTemplate}
          options={editorStore.templates?.map((x) => ({
            label: x.name,
            value: x._id
          }))}
        />
      </Grid>
      <Grid item xs={5} style={{ padding: 16 }}>
        <Select
          // clearable={false}
          name={`${name}.favTemplate`}
          key={`${name}.favTemplate`}
          label={locale.FavTemplate}
          fullWidth
          value={editorStore.form.getValue(`${name}.favTemplate`)}
          onChange={handleChangeFavTemplate}
          options={editorStore.fav?.map((x) => ({
            label: x.name,
            value: x._id
          }))}
        />
      </Grid>

      <Grid item xs={12} style={{ padding: 16 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: "50%" }}>{locale.Name}</TableCell>
              <TableCell style={{ width: "10rem" }}>{locale.Price}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {editorStore.form.getValue(`${name}.professions`)?.map((item, i) => {
              return (
                <TableRow key={i}>
                  <TableCell>
                    <Typography>{getLastLvlOfAOE(item)}</Typography>
                  </TableCell>

                  <TableCell>
                    <CurrencyText
                      type="number"
                      name={`${name}.professionsPrice.${i}.price`}
                      InputProps={{ fullWidth: true, type: "text" }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <ContractsPriceTemplateEditorForm<CustomerContractPriceListFormData>
          formStore={editorStore.form}
          duplicateRecord={(item) => editorStore.duplicateRecord(item, name)}
          addBlankRecord={() => editorStore.addBlankRecord(name)}
          type={ContractsPriceTemplateType.CustomerContract}
          // loading={}
          name={`priceGroups.${priceGroupIndex}.records`}
          holidaysName={`priceGroups.${priceGroupIndex}.highHolidays`}
          variant={"customerContract"}
          validationResult={editorStore.validationResult}
          isNationalProcurement={editorStore.isNationalProcurement ?? false}
          isNationalProcurementPriceStrategy={editorStore.isNationalProcurement ?? false}
          isBonlivaCare={editorStore.isBonlivaCare ?? false}
        />
        <div style={{ marginTop: 40 }}>
          <FormControls
            fitted
            right={
              <PrimaryButton onClick={() => nav.close()}>{locale.Save}</PrimaryButton>
            }
          />
        </div>
      </Grid>
      <FormxDebugger />
    </Grid>
  ));
}
